import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, startAfter, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

const PAGE_SIZE = 15;

function HeartIcon({ filled }) {
  return (
    <svg className="w-4 h-4" fill={filled ? '#ef4444' : 'none'} stroke="#ef4444" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function FavoritesPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [total, setTotal] = useState(0);

  const load = useCallback(async (after = null) => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const col = collection(db, `users/${currentUser.uid}/favoritedVerses`);
      const constraints = [orderBy('favoritedAt', 'desc'), limit(PAGE_SIZE)];
      if (after) constraints.push(startAfter(after));
      const snap = await getDocs(query(col, ...constraints));
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setVerses(prev => after ? [...prev, ...rows] : rows);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(rows.length === PAGE_SIZE);
      if (!after) {
        const totalSnap = await getDocs(col);
        setTotal(totalSnap.size);
      }
    } catch (e) {
      console.error('FavoritesPage load error', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const unfavorite = async (verse) => {
    try {
      await deleteDoc(doc(db, `users/${currentUser.uid}/favoritedVerses/${verse.id}`));
      setVerses(prev => prev.filter(v => v.id !== verse.id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('unfavorite error', e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-[#0d1520]" style={{ paddingBottom: '80px' }}>
      <div className="max-w-2xl mx-auto px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <h1 className="text-xl font-semibold text-space-cadet dark:text-white">
              Favorited Verses
              {total > 0 && <span className="ml-2 text-sm font-normal text-gray-400 dark:text-white/40">({total})</span>}
            </h1>
          </div>
        </div>

        {/* Empty state */}
        {!loading && verses.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-300 dark:text-white/20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-white/40 font-medium mb-1">No favorited verses yet</p>
            <p className="text-sm text-gray-400 dark:text-white/30">Heart a verse while listening to save it here</p>
            <button
              onClick={() => navigate('/listen')}
              className="mt-4 px-5 py-2 rounded-xl bg-cerulean text-white text-sm font-medium hover:bg-cerulean/90 transition-colors"
            >
              Go to Listen
            </button>
          </div>
        )}

        {/* Verse list */}
        {verses.length > 0 && (
          <div className="space-y-3">
            {verses.map(verse => (
              <div
                key={verse.id}
                className="bg-white dark:bg-[#111827] rounded-[3px] border border-gray-200 dark:border-white/10 p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5 w-8 h-8 bg-red-100 dark:bg-red-400/15 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-500 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-space-cadet dark:text-white">{verse.passage}</h3>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-gray-400 dark:text-white/30">
                          {verse.favoritedAt ? new Date(verse.favoritedAt.seconds * 1000).toLocaleDateString() : ''}
                        </span>
                        <button
                          onClick={() => unfavorite(verse)}
                          title="Remove from favorites"
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-400/10 transition-colors"
                        >
                          <HeartIcon filled />
                        </button>
                      </div>
                    </div>
                    {verse.verseText && (
                      <p className="text-sm text-gray-600 dark:text-white/60 mb-2 italic">"{verse.verseText}"</p>
                    )}
                    <button
                      onClick={() => navigate(`/listen?book=${verse.book}&chapter=${verse.chapter}&verse=${verse.verse}`)}
                      className="text-xs text-cerulean dark:text-cerulean-lighter hover:underline"
                    >
                      Listen to this passage →
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Show more */}
            {hasMore && (
              <div className="text-center pt-2 pb-4">
                <button
                  onClick={() => load(lastDoc)}
                  disabled={loading}
                  className="px-6 py-2 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-700 dark:text-white/70 rounded-[3px] font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading…' : 'Show more'}
                </button>
              </div>
            )}

            {loading && verses.length > 0 && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cerulean mx-auto" />
              </div>
            )}
          </div>
        )}

        {loading && verses.length === 0 && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean mx-auto mb-3" />
            <p className="text-gray-400 dark:text-white/30 text-sm">Loading favorites…</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default FavoritesPage;
